import { useState } from 'react';
import { z } from 'zod';
import { Button, Modal, FormRow, FormSection } from '@shared/components';
import type { Department } from '@shared/types';
import type { DepartmentCreateInput } from '../services/organization.service';

interface DepartmentFormModalProps {
  target: Department | null;
  onClose: () => void;
  onSave: (data: DepartmentCreateInput) => Promise<void>;
}

const formSchema = z.object({
  name: z.string().min(1, '部署名を入力してください'),
  organizationId: z.string().min(1),
  parentId: z.string().nullable().default(null),
  managerId: z.string().nullable().default(null),
});

const cls = 'w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500';

export function DepartmentFormModal({ target, onClose, onSave }: DepartmentFormModalProps) {
  const [name, setName] = useState(target?.name ?? '');
  const [managerId, setManagerId] = useState(target?.managerId ?? '');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSave = async () => {
    setError(null);
    const parsed = formSchema.safeParse({
      name,
      organizationId: target?.organizationId ?? '',
      parentId: target?.parentId ?? null,
      managerId: managerId || null,
    });
    if (!parsed.success) {
      setError(parsed.error.errors[0].message);
      return;
    }
    setIsLoading(true);
    try {
      await onSave(parsed.data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'エラーが発生しました。');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={target ? '部署を編集' : '部署を追加'} size="sm">
      <div className="flex flex-col gap-3">
        {error && <p className="text-sm text-red-600">{error}</p>}
        <FormSection title="部署情報">
          <FormRow label="部署名" required>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); }}
              className={cls}
            />
          </FormRow>
          <FormRow label="管理者UID">
            <input
              type="text"
              value={managerId}
              onChange={(e) => { setManagerId(e.target.value); }}
              className={cls}
            />
          </FormRow>
        </FormSection>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            キャンセル
          </Button>
          <Button
            onClick={() => { void handleSave(); }}
            disabled={isLoading}
          >
            {isLoading ? '保存中...' : '保存'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
